import React, { useState } from "react";

import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  Grid,
  Typography,
  Button,
} from "@material-ui/core";

import { useHistory } from "react-router-dom";
import {
  Add as AddIcon,
  Edit as EditIcon,
  ArrowBack,
} from "@material-ui/icons";
import { useCompany } from "../../../contexts/CompanyContext";

/**
 * ProductTypes component renders a list of product types, with an option to add,
 * edit or delete each type.
 *
 * @component
 * @returns {React.Element} The rendered component.
 *
 * @example
 * <ProductTypes />
 */
function ProductTypes() {
  const defFormValues = {
    name: "",
    is_deleted: false,
  };

  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedProductType, setSelectedProductType] = useState({});
  const [saving, setSaving] = useState(false);
  const [formValues, setFormValues] = useState(defFormValues);
  const history = useHistory();
  const { productTypes, upsertProductType } = useCompany();

  const handleDialogOpen = productType => {
    setSelectedProductType(productType);
    if (productType) {
      setFormValues(prevFormValues => ({ ...prevFormValues, ...productType }));
    }
    setDialogOpen(true);
  };

  const handleFormValues = event => {
    const { name, value } = event.target;
    if (name === "is_active") {
      setFormValues(prevFormValues => ({
        ...prevFormValues,
        is_active: !prevFormValues.is_active,
      }));
    } else {
      setFormValues(prevFormValues => ({ ...prevFormValues, [name]: value }));
    }
  };

  const handleDialogClose = () => {
    setSelectedProductType({});
    setFormValues(defFormValues);
    setDialogOpen(false);
  };

  const handleUpsert = async payload => {
    setSaving(true);
    try {
      await upsertProductType(payload);
      handleDialogClose();
    } catch (error) {
      console.error("Error performing upsert:", error);
    } finally {
      setSaving(false);
    }
  };

  const handleSubmitWithCommon = async e => {
    e.preventDefault();
    handleUpsert({ ...formValues });
  };

  const handleDeletewithCommon = async e => {
    e.preventDefault();
    handleUpsert({ ...formValues, is_deleted: true });
  };

  return (
    <div>
      <div>
        <Button
          startIcon={<ArrowBack />}
          variant="contained"
          color="primary"
          onClick={() => history.goBack()}
          style={{ marginBottom: 20 }}
        >
          Back
        </Button>
        <Typography variant="h6" component="div">
          Product Types
        </Typography>
        <List>
          <Grid container spacing={2} xs={12} md={8}>
            {productTypes.map(productType => (
              <Grid
                style={{ marginLeft: 20 }}
                item
                xs={12}
                md={8}
                key={productType.id}
              >
                <ListItem>
                  <div>
                    <ListItemText primary={productType.name} />
                  </div>
                  <ListItemSecondaryAction>
                    <IconButton
                      edge="end"
                      onClick={() => handleDialogOpen(productType)}
                    >
                      <EditIcon />
                    </IconButton>
                  </ListItemSecondaryAction>
                </ListItem>
              </Grid>
            ))}
          </Grid>
        </List>
        <div style={{ marginTop: 20 }}>
          <Button
            variant="contained"
            color="primary"
            startIcon={<AddIcon />}
            onClick={() => handleDialogOpen()}
          >
            Add Product Type
          </Button>
        </div>

        <Dialog
          maxWidth="sm"
          fullWidth
          open={dialogOpen}
          onClose={handleDialogClose}
        >
          <DialogTitle>
            {selectedProductType ? "Edit Product Type" : "Add Product Type"}
          </DialogTitle>
          <DialogContent>
            <form>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <TextField
                    style={{ marginTop: 20 }}
                    label="Name"
                    name="name"
                    fullWidth
                    value={formValues.name}
                    onChange={handleFormValues}
                  />
                </Grid>
              </Grid>
            </form>
          </DialogContent>
          <DialogActions>
            <Grid
              container
              item
              spacing={1}
              direction="row"
              justifyContent="flex-end"
            >
              <Grid item>
                {selectedProductType && (
                  <Button
                    type="button"
                    color="secondary"
                    variant="contained"
                    sx={{ mt: 2 }}
                    onClick={handleDeletewithCommon}
                  >
                    {saving ? "Loading..." : "Delete"}
                  </Button>
                )}
              </Grid>
              <Grid item>
                <Button
                  color="primary"
                  variant="contained"
                  sx={{ mt: 2 }}
                  onClick={handleSubmitWithCommon}
                >
                  {saving ? "Loading..." : "Save"}
                </Button>
              </Grid>
            </Grid>
          </DialogActions>
        </Dialog>
      </div>
    </div>
  );
}

export default ProductTypes;
